<template>
  <div class="page lightmap">
    <navigation-header />
    <div class="main">
      <div class="eyebrow-heading">
        {{ $t(`reset_password.${state}.eyebrow`) }}
      </div>

      <product-header :heading="heading" :show-logo="!isLoadingState && !isErrorState" />

      <vertical-stack class="paragraph-content">
        <paragraph-content v-if="haveParagraph" :keypath="paragraphKeypath" :email="emailAddress" />

        <div v-if="te(`reset_password.${state}.subtext`)" class="subtext-content">
          {{ $t(`reset_password.${state}.subtext`) }}
        </div>

        <h3 v-if="te(`reset_password.${state}.heading_small`)" class="heading-small">
          {{ $t(`reset_password.${state}.heading_small`) }}
        </h3>

        <form v-if="state === ViewState.NewPassword" class="button-container" @submit.prevent="onResetPassword">
          <vertical-stack>
            <text-box
              ref="passwordInput" v-model="password" autocomplete="current-password" :minlength="6"
              :label="$t(`reset_password.new_password.choose_a_new_password`)" type="password" required
            />
            <text-box
              ref="confirmPasswordInput" v-model="confirmPassword"
              :label="$t(`reset_password.new_password.confirm_your_new_password`)" type="password" required
            />
            <horizontal-stack>
              <large-button variant="secondary" @click="redirectToLogin()">
                {{ $t('common.button.cancel') }}
              </large-button>
              <large-button type="submit" :loading="isLoading">
                {{ $t('common.button.continue') }}
              </large-button>
            </horizontal-stack>
          </vertical-stack>
        </form>

        <a v-if="isErrorState" class="paragraph-content" href="/" @click.prevent="onRetryResetPassword">
          {{ $t('common.link.retry_reset_password') }}
        </a>

        <a v-if="isErrorState" class="paragraph-content" href="/" @click.prevent="onBackToStartPage">
          {{ $t('common.link.go_back_to_the_start') }}
        </a>
      </vertical-stack>

      <div v-show="state === ViewState.ResetPasswordSuccess">
        <authenticator ref="authComponent" class="authenticator" @state-changed="onAuthStateChanged" />
      </div>

      <Loading v-if="isLoadingState" class="loader" />
    </div>
    <accounts-footer />
  </div>
</template>

<script setup>
import { onMounted, watch, ref, computed, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  AuthState, Authenticator, useAuthentication, useProduct,
  TextBox, LargeButton, HorizontalStack, VerticalStack,
  Loading, useLoadingState, useAnalytics
}
  from 'ihunter-pathfinder-components';
import { useRouteManager } from '../composables/useRouteManager';
import { useTestState } from '../composables/useTestState';
import NavigationHeader from '../components/NavigationHeader.vue';
import ProductHeader from '../components/ProductHeader.vue';
import AccountsFooter from '../components/AccountsFooter.vue';
import ParagraphContent from '../components/ParagraphContent.vue';
import { sleepForMinTime } from 'ihunter-common';

const { product } = useProduct();
const { isLoading, doLoadingOperation } = useLoadingState();
const { validateResetPasswordCode, resetPassword, signOut, user } = useAuthentication();
const { redirectToHomeOrContinue, redirectToLogin, redirectToLoginWithAuthState } = useRouteManager();
const { initializeTestStateFromUrl, TestStateUser, testState } = useTestState();
const { logScreenView } = useAnalytics();

const route = useRoute();
const { t, te } = useI18n();

const ViewState = {
  // Form to enter new password
  NewPassword: 'new_password',

  // Password reset successfully
  ResetPasswordSuccess: 'reset_password_success',

  // Progress states
  ValidatingPasswordCode: 'validating_password_code',
  ResettingPassword: 'resetting_password',

  // Error states
  PasswordCodeInvalid: 'password_code_invalid',
  ResetPasswordFailed: 'reset_password_failed',
};
Object.freeze(ViewState);

const state = ref(ViewState.ValidatingPasswordCode);
const authComponent = ref(null);

const actionCode = ref(null);
const emailAddress = ref(null);
const password = ref('');
const passwordInput = ref(null);
const confirmPassword = ref('');
const confirmPasswordInput = ref(null);

const MIN_LOADING_VIEW_TIME_MS = 2000;

onMounted(async () => {
  console.log(`Initialize ResetPasswordView`);
  initializeTestStateFromUrl(ViewState);
  if (testState.value) {
    setViewState(testState.value);
    emailAddress.value = TestStateUser.email;
  } else {
    await validateActionCode();
  }
});

const heading = computed(() => {
  return t(`reset_password.${state.value}.heading`);
});

const haveParagraph = computed(() => {
  return te(`reset_password.${state.value}.paragraph`);
});

const paragraphKeypath = computed(() => {
  return `reset_password.${state.value}.paragraph`;
});

const isLoadingState = computed(() => {
  return state.value === ViewState.ValidatingPasswordCode || state.value === ViewState.ResettingPassword;
});

const isErrorState = computed(() => {
  return state.value === ViewState.PasswordCodeInvalid || state.value === ViewState.ResetPasswordFailed;
});

// TODO-Pathfinder (HUNT-1777): app-wide strategy for focusing the first field
// const focusField = (fieldRef) => {
//   nextTick(() => {
//     if (fieldRef.value) {
//       fieldRef.value.focus();
//     }
//   });
// }

const validateActionCode = async () => {
  try {
    setViewState(ViewState.ValidatingPasswordCode);
    actionCode.value = route.query.oobCode;
    emailAddress.value = await sleepForMinTime(validateResetPasswordCode(actionCode.value), MIN_LOADING_VIEW_TIME_MS);
    console.log(`Reset password code ${actionCode.value} is valid for email ${emailAddress.value}`);
    setViewState(ViewState.NewPassword);
    // focusField(passwordInput);
  } catch (error) {
    setViewState(ViewState.PasswordCodeInvalid);
    console.error(`Reset password code ${actionCode.value} is invalid`, error);
  }
}

const onAuthStateChanged = async (authState, authEmail) => {
  if (state.value === ViewState.ResetPasswordSuccess) {
    if (authState === AuthState.SignOut) {
      console.log(`Redirecting to Home to handle auth mode ${authState}`);
      await redirectToHomeOrContinue();
    } else if (authState !== AuthState.SignInWithPassword) {
      console.log(`Redirecting to Login to handle auth mode ${authState}`);
      redirectToLoginWithAuthState(authState, authEmail);
    }
  }
};

const validatePasswordFields = () => {
  if (password.value === confirmPassword.value) {
    return true;
  }
  confirmPasswordInput.value.invalidate(t('common.error.confirm_password_does_not_match'));
  return false;
}

const onResetPassword = async () => {
  await doLoadingOperation(async () => {
    if (validatePasswordFields()) {
      try {
        await resetPassword(actionCode.value, password.value);
        await signOut();
        setViewState(ViewState.ResetPasswordSuccess);
      } catch (error) {
        setViewState(ViewState.ResetPasswordFailed);
      }
    }
  });
}

const onBackToStartPage = () => {
  if (!testState.value && user.value) {
    redirectToHomeOrContinue();
  } else {
    redirectToLogin();
  }
}

const setViewState = (viewState) => {
  console.log(`Setting view state: ${viewState}`);
  logScreenView(viewState);
  state.value = viewState;
  if (viewState === ViewState.ResetPasswordSuccess) {
    nextTick(() => { // ensure authComponent is mounted after state change
      authComponent.value.setAuthState(AuthState.SignInWithPassword);
      authComponent.value.setAuthEmail(emailAddress.value);
    });
  }
}

const onRetryResetPassword = () => {
  redirectToLoginWithAuthState(AuthState.RequestResetPassword, emailAddress.value);
}
</script>

<style lang="scss" scoped>
@use 'ihunter-pathfinder/styles' as Pathfinder;
@use '../styles/Accounts' as Accounts;

.page {
  @include Accounts.Default;
  @include Accounts.Container;

  .heading-small {
    margin-top: Pathfinder.$space-300;
  }

  a {
    width: 100%;
  }

  .loader {
    margin-top: Pathfinder.$space-500;
  }
}
</style>
